import React, { useMemo, useRef } from 'react';
import { FormApi, FormContext, FormStateBasic } from 'informed';
import { useTranslation } from 'react-i18next';

import {
  globalScrollToErrorField,
  publicRegistrationFormGlobalFieldClassName
} from '@nw/shared/utils';

import {
  PublicFormMachineSendType,
  PublicFormMachineStateType
} from '../../../form/machines/PublicFormsMachine/types';
import { FormTypes } from '../../../form/types/ProductTypes';
//TODO: Use product types from shared
import {
  PublicProductFormMachineSendType,
  PublicProductFormMachineStateType
} from '../../../product/machines/PublicFormsMachine/types';
import { ProductTypes } from '../../../product/types/ProductTypes';

import { progressBarType } from '../../constants/FormConstant';
import FormFieldGroup from '../../stores/models/FieldModels/FormFieldGroupField';
import FormFieldWithInitialValue from '../../stores/models/FieldModels/FormFieldWithInitialValue';
import StepConfig from '../../stores/models/StepConfig';
import { CustomClassNamesInterface } from '../../types/MiscellaneousTypes';
import RegistrationFormYupValidationSchema from '../../utils/YupValidationUtils';

import Multistep from '../ExtendedMultiStepField';
import { MultiStepRegistrationForm } from '../MultiStepRegistrationForm';

import { PublicRegistrationFormContainer } from './styledComponent';

interface PublicRegistrationFormProps {
  publicFormMachineState:
    | PublicProductFormMachineStateType
    | PublicFormMachineStateType;
  publicFormMachineSend:
    | PublicProductFormMachineSendType
    | PublicFormMachineSendType;
  formConfig: ProductTypes | FormTypes;
  customClassNames: CustomClassNamesInterface;
  customHeader?: (eachFormStep: StepConfig) => React.ReactElement;
  renderCustomSuccessPage?: (
    formValues: FormStateBasic['values'],
    publicFormMachineState: PublicFormMachineStateType,
    publicFormMachineSend: PublicFormMachineSendType
  ) => React.ReactElement;
}

export const PublicRegistrationForm: React.FC<PublicRegistrationFormProps> = ({
  publicFormMachineSend,
  publicFormMachineState,
  formConfig,
  customClassNames,
  renderCustomSuccessPage,
  customHeader
}) => {
  const formRef = useRef<any>();
  const { t } = useTranslation();

  const validationSchemaObject = useMemo(
    () => RegistrationFormYupValidationSchema(formConfig, t),
    []
  );

  //Note: handle any specific action on form submit
  const onSubmitForm = (): void => {};

  const onValueChange = (values: any): void => {
    let fieldData = formConfig.form.allFormFields ?? [];

    const formFieldGroupField: FormFieldWithInitialValue[] = [];

    fieldData.forEach((eachField) => {
      if (eachField instanceof FormFieldGroup) {
        eachField.formFields.forEach((field) => {
          formFieldGroupField.push(field);
        });
      }
    });

    fieldData = [...fieldData, ...formFieldGroupField];

    const isMultiStep = formConfig.form?.steps?.length > 0;
    fieldData.forEach((element) => {
      const {
        isRelevant,
        relevantCondition,
        field,
        isExpectedRemoval,
        supportingField
      } = element;
      if (
        isMultiStep &&
        isRelevant &&
        relevantCondition &&
        !eval(relevantCondition) &&
        isExpectedRemoval &&
        formRef.current
      ) {
        formRef.current.emitter.fieldsById.forEach(function (value, key) {
          if (value && value.field === field) {
            setTimeout(() => {
              formRef.current.emitter.fieldsById['delete'](key);
            }, 0);
          }
        });

        delete formRef.current.emitter.state.errors[field];

        values[field] && delete values[field];
        supportingField && delete values[supportingField];

        formRef.current.emitter.expectRemoval(field);
        supportingField &&
          formRef.current.emitter.expectRemoval(supportingField);
      }
    });
  };

  const getFormattedFormValues = (values: any): Record<string, any> => {
    let updatedValues = {};
    const { current_state, currentDistrict, ...rest } = values;
    if (current_state) {
      const currentState = current_state['state_name'];
      updatedValues = {
        currentState,
        current_state: currentState
      };
    }
    if (currentDistrict && currentDistrict['district_name']) {
      const currentDistrictName = currentDistrict['district_name'];
      updatedValues = {
        ...updatedValues,
        currentDistrict: currentDistrict,
        current_district: currentDistrictName
      };
    }

    const graduationYearOrExpected =
      values.graduationYearOrExpected ||
      values.graduationYear ||
      values.expectedGraduationYear ||
      values.diplomaCompletionYear ||
      values.yearOfGraduation;

    if (graduationYearOrExpected) {
      updatedValues = {
        ...updatedValues,
        yearOfGraduation: graduationYearOrExpected
      };
    }

    return {
      ...rest,
      ...updatedValues
    };
  };

  const getNumberedProgressBarTitles = (): string[] => {
    const { steps } = formConfig.form;
    return steps.reduce<string[]>((acc, eachStep) => {
      if (
        eachStep.stepInfo?.progress_bar_info?.type ===
        progressBarType.numberedStepProgressBar
      ) {
        const title = eachStep.stepInfo?.progress_bar_info?.step_details?.title;
        if (title && !acc.includes(title)) {
          acc.push(title);
        }
      }
      return acc;
    }, []);
  };

  const handleFormReset = () => {
    if (formRef.current) {
      formRef.current.reset();
    }
  };

  const renderMultiStepForm = (formProps: FormContext): React.ReactElement => {
    const { formApi, formState } = formProps;
    const { values, touched, errors } = formState;
    const { steps, initialStepName } = formConfig.form;

    return (
      <Multistep initialStep={initialStepName}>
        {steps.map((eachFormStep, index) => {
          return (
            <MultiStepRegistrationForm
              key={index}
              eachFormStep={eachFormStep}
              formValues={getFormattedFormValues(values)}
              touched={touched}
              errors={errors}
              formApi={formApi}
              publicFormMachineSend={publicFormMachineSend}
              publicFormMachineState={publicFormMachineState}
              validationSchemaObject={validationSchemaObject}
              customClassNames={customClassNames}
              customHeader={customHeader}
              renderCustomSuccessPage={renderCustomSuccessPage}
              getNumberedProgressBarTitles={getNumberedProgressBarTitles}
              t={t}
              handleFormReset={handleFormReset}
              designSystemType={
                formConfig && formConfig.designSystemType
                  ? formConfig.designSystemType
                  : undefined
              }
            />
          );
        })}
      </Multistep>
    );
  };

  const shouldUseShadcnDesignSystem =
    'designSystemType' in formConfig &&
    formConfig.designSystemType === 'SHADCN_DESIGN_SYSTEM';

  return (
    <PublicRegistrationFormContainer
      onValueChange={onValueChange}
      onSubmit={onSubmitForm}
      validationSchema={validationSchemaObject}
      initialValues={formConfig.formInitialValues}
      allowEmptyStrings={true}
      getApi={(formApi: FormApi) => (formRef.current = formApi)}
      className={customClassNames.publicRegistrationFormContainer}
      onSubmitFailure={
        shouldUseShadcnDesignSystem
          ? () => {
              setTimeout(() => {
                globalScrollToErrorField(
                  publicRegistrationFormGlobalFieldClassName
                );
              });
            }
          : undefined
      }
    >
      {(formProps: FormContext) => {
        return renderMultiStepForm(formProps);
      }}
    </PublicRegistrationFormContainer>
  );
};
